import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchAccounts, saveAccountDetails, fetchAccountById } from '../../services/masters/accountService';
import { ACTIONS } from '../../constants/apiEndpoints';

export const getAccounts = createAsyncThunk(
  ACTIONS.account_master,
  async (_, thunkAPI) => {
    try {
      const response = await fetchAccounts();
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Login Failed: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

export const saveAccount = createAsyncThunk(
  ACTIONS.account_save,
  async (accountData, thunkAPI) => {
    try {
      const response = await saveAccountDetails(accountData);
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Login Failed: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

export const getAccountById = createAsyncThunk(
  ACTIONS.account_by_id,
  async (id, thunkAPI) => {
    try {
      const response = await fetchAccountById(id);
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Failed to fetch account: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState: {
    statuscode: 0,
    message: null,
    description: null,
    data: {},
    options: [],
    saveStatus: 'idle',
    loading: false,
    error: null,
  },
  reducers: {
    resetSaveStatus: (state) => {
      state.saveStatus = 'idle';
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAccounts.fulfilled, (state, action) => {
        state.loading = false;
        const { statuscode, message, description, data } = action.payload;
        state.statuscode = statuscode;
        state.message = message;
        state.description = description;
        state.data = data;
      })
      .addCase(getAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Save Account
      .addCase(saveAccount.pending, (state) => {
        state.loading = true;
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(saveAccount.fulfilled, (state, action) => {
        state.loading = false;
        const { statuscode, message, description, data } = action.payload;
        state.statuscode = statuscode;
        state.message = message;
        state.description = description;

        if (state.data?.accounts) {
          const index = state.data.accounts.findIndex(
            (account) => account.accountName === data.accountName
          );

          if (index !== -1) {
            // Override existing account
            state.data.accounts[index] = data;
          } else {
            // Push new account
            state.data.accounts.push(data);
          }

          state.saveStatus = 'success';
        }
      })
      .addCase(saveAccount.rejected, (state, action) => {
        state.loading = false;
        state.saveStatus = 'error';
        state.error = action.payload;
      })
      // Get Account by ID
      .addCase(getAccountById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAccountById.fulfilled, (state, action) => {
        state.loading = false;
        // We don't need to update the state data here as we're just fetching for the form
      })
      .addCase(getAccountById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});
export const { resetSaveStatus } = accountSlice.actions;
export default accountSlice.reducer;
